import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { getCatalogoDePadrones } from 'api/catalogos';
import { patchCiudadano } from 'api/cuentaunicasir/ciudadanos';
import { postGenerarGastos } from 'api/recaudacion/index';
import useAdeudoPadron from 'hooks/useAdeudoPadron';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { onError } from 'utils/handlers';
import { gettersPadronesCajaMap } from 'utils/padrones';
import padronToString, { getPadronKey } from 'utils/padronesToString';
import address from 'utils/address';
import { listToString } from 'utils/normalizers';
import getFormatedValues, { formatReceived } from 'utils/formatValues';
import { aplicacionesCargosEnum, padronesEnum, tiposDatosFormulaEnum } from 'views/Tramites/GPM/enums';
import useConfirm from 'hooks/useConfirm';
import { patchCancelarGasto, patchGasto } from 'api/recaudacion/gastos';
import { normalizeCargos } from '../utils/normalizeCargos';

const useGastos = () => {
  const [ConfirmModal, confirm] = useConfirm('Cancelar gasto', '¿Está seguro de querer cancelar el gasto?');
  const _entidad = useSelector(({ auth }) => auth.entidad);
  const moneda = useSelector(({ auth }) => auth.entidad.moneda);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const { padron_principal = 3 } = _entidad;
  const collapsedSidebar = useSelector(({ app }) => app.collapsedSidebar);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones)?.filter((e) => e.id !== 5);

  const [loading, setLoading] = useState(false);
  const [tipoDePadronSelected, setTipoDePadronSelected] = useState(padron_principal);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysGastos, setSelectedRowKeysGastos] = useState([]);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [dataExterno, setDataExterno] = useState([]);
  const [padron, setPadron] = useState(false);
  const [gastos, setGastos] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [tiposDeGastos, setTiposDeGastos] = useState([]);

  const [
    loadingAdeudo,
    loadingMessage,
    cargos,
    getCargosPadron,
  ] = useAdeudoPadron(tipoDePadronSelected, padron?.id, normalizeCargos);

  const isLoading = loading || loadingAdeudo;

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = hasActionPermission('padron', ActionsNames.view);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) setHasAllViewPermission(_hasAllViewPermission);
    };
    const fetchAll = async () => {
      setLoading(true);
      if (mounted) await getCatalogoDePadrones();
      setLoading(false);
    };
    fetchAll();
    validatePermission();
    return () => { mounted = false; };
  }, []);

  const normalizePadron = (_padron) => {
    const normalizedPadron = { ..._padron };
    normalizedPadron._nombre_completo = padronToString(_padron, tipoDePadronSelected);
    if (tipoDePadronSelected === padronesEnum.PREDIO) {
      normalizedPadron._nombre_completo = getPadronKey(_padron, padronesEnum.PREDIO);
      normalizedPadron.direccion = typeof _padron.direccion === 'string'
        ? _padron.direccion : address(_padron.direccion);
      normalizedPadron._propietario = _padron?.nombres_de_participes?.length
        ? listToString(_padron.nombres_de_participes)
        : padronToString(_padron?.ciudadano, 1);
    }
    normalizedPadron.tipo_de_padron = tipoDePadronSelected;
    return normalizedPadron;
  };

  const handleSearchPadron = async (form) => {
    try {
      setLoading(true);
      const { q } = form.getFieldsValue();

      const dataPadron = await gettersPadronesCajaMap[tipoDePadronSelected]({ q });

      if (!([0, 1].includes(dataPadron?.results?.length) || [1, 0].includes(dataPadron?.length))) {
        setDataExterno(dataPadron);
        setVisibleBusquedaPadrones(true);
        setPadron();
        return;
      }

      const found = dataPadron?.results?.[0] || dataPadron?.[0];

      if (!found) {
        message.warn('Padrón no encontrado', 3.5);
        setPadron();
        return;
      }

      const normalizedPadron = normalizePadron(found);
      setPadron(normalizedPadron);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const onCancelGasto = () => {
    setVisibleForm(false);
    setSelectedRowKeysGastos([]);
    setDataExterno([]);
  };

  const calcularVariables = (gasto, values, variables) => {
    if (![aplicacionesCargosEnum.FORMULA, aplicacionesCargosEnum.SQL_FUNCTION]
      .includes(gasto.tipo_de_aplicacion)) {
      return null;
    }

    return gasto.variables.map((e) => {
      const match = variables.find((item) => item.id === e);
      let valor = values[match.nombre_de_variable];

      if (match.tipo_de_variable === tiposDatosFormulaEnum.RANGO_DE_FECHAS) {
        const [fechaInicial, fechaFinal] = valor;
        valor = fechaFinal.diff(fechaInicial, 'days');
      }

      return {
        id: e,
        nombre_de_variable: match.nombre_de_variable,
        valor,
      };
    });
  };

  const onFinishGasto = async (formGasto, variables) => {
    try {
      await formGasto.validateFields();
      const formGastoValues = formGasto.getFieldsValue();
      const [cargo] = selectedRowKeys;
      const tipoDeGastoId = formGasto.getFieldValue('tipo_de_gasto');
      const tipoDeGasto = tiposDeGastos.find((e) => e.id === tipoDeGastoId);
      const { descripcion } = tipoDeGasto;
      const [key] = selectedRowKeysGastos;
      setLoading(true);

      let values = {
        cargo,
        descripcion,
        ...formGastoValues,
      };
      const vars = calcularVariables(tipoDeGasto, formGastoValues, variables);

      if (tipoDeGasto.tipo_de_aplicacion === aplicacionesCargosEnum.FORMULA) {
        delete values.importe;
        values = getFormatedValues({
          ...values,
          variables: vars,
        });
      }

      const created = await (key ? patchGasto(key, values) : postGenerarGastos(values));
      if (created) {
        onCancelGasto();
        setVisible(false);
        setVisibleForm(false);
        formGasto.resetFields();
        setSelectedRowKeys([cargo]);
        await getCargosPadron();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const asignarEntidad = async (user) => {
    setLoading(true);
    const { razon_social } = _entidad;
    const { entidad, nombre_completo, id } = user;
    entidad.push(entidad.id);
    const edited = await patchCiudadano(
      id,
      { entidad },
      `Ahora el usuario ${nombre_completo} también pertenece a ${razon_social}.`,
    );
    if (edited) {
      await getCargosPadron();
    }
    setLoading(false);
  };

  const onClickExpand = (evt, _cargos = cargos, formCargo) => {
    const [key] = selectedRowKeys;
    const record = _cargos.find((e) => e.id === key);
    const formatValues = (values) => {
      const output = { ...values };
      // delete output.descripcion;
      delete output.id;
      return output;
    };
    const formatedValues = formatValues(record);
    formatedValues.periodo_fiscal = record.tipo_de_cargo?.periodo_fiscal?.periodo;

    formCargo.setFieldsValue({ ...formatedValues });
    setGastos(formatReceived(record.gastos));
    setTiposDeGastos(record.tipos_de_gasto);
    setVisible(true);
  };

  const onClickExpandGasto = async (formGasto) => {
    try {
      const [key] = selectedRowKeysGastos;
      const match = gastos.find((e) => e.id === key);
      formGasto.setFieldsValue(match);
      setVisibleForm(true);
    } catch (err) {
      onError(err);
    }
  };

  const onClickAdd = (formGasto) => {
    onCancelGasto();
    formGasto.resetFields();
    setVisibleForm(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const handleOnRowClickGasto = (record) => {
    setSelectedRowKeysGastos([record.id]);
  };

  const onClear = (form) => {
    setDataExterno();
    setTipoDePadronSelected(padron_principal);
    form.resetFields();
    setPadron();
    setSelectedRowKeys([]);
  };

  const onCancelCargo = (formCargo) => {
    formCargo.resetFields();
    setSelectedRowKeys([]);
    setVisible(false);
  };

  const handleOnCancelGasto = async () => {
    const confirmed = await confirm();
    if (!confirmed) return;
    setLoading(true);

    const [key] = selectedRowKeysGastos;
    const canceled = await patchCancelarGasto(key);
    if (canceled) {
      setGastos(gastos.filter((e) => e.id !== key));
      setSelectedRowKeysGastos([]);
      setVisibleForm(false);
      setVisible(false);
      await getCargosPadron();
    }
    setLoading(false);
  };

  return {
    // Estado
    loading: isLoading,
    loadingMessage,
    tipoDePadronSelected,
    setTipoDePadronSelected,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedRowKeysGastos,
    setSelectedRowKeysGastos,
    visibleBusquedaPadrones,
    setVisibleBusquedaPadrones,
    visible,
    setVisible,
    visibleForm,
    setVisibleForm,
    dataExterno,
    setDataExterno,
    padron,
    setPadron,
    gastos,
    setGastos,
    hasAllViewPermission,
    tiposDeGastos,
    setTiposDeGastos,
    cargos,
    getCargosPadron,
    padron_principal,
    padrones,
    collapsedSidebar,
    estadosGlobales,
    moneda,

    // Métodos
    normalizePadron,
    handleSearchPadron,
    onFinishGasto,
    asignarEntidad,
    onCancelGasto,
    onClickExpand,
    onClickExpandGasto,
    onClickAdd,
    handleOnRowClick,
    handleOnRowClickGasto,
    onClear,
    onCancelCargo,
    handleOnCancelGasto,
    ConfirmModal,
  };
};

export default useGastos;
